import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSessionRevision,
  readSession,
  removeSession,
  writeSession
} from '../util/session-storage';

const restoreFile = (key) => {
  const saved = readSession(key, null);
  try {
    if (!saved || typeof saved.name !== 'string' || typeof saved.data !== 'string') return null;
    const bytes = Uint8Array.from(atob(saved.data), (char) => char.charCodeAt(0));
    return new File([bytes], saved.name, { type: saved.type, lastModified: saved.lastModified });
  } catch {
    return null;
  }
};

// Files live in sessionStorage too: no persistent copy remains after tab closure.
// If a report exceeds browser quota, keep the live selection usable and explain
// that this particular file must be reselected after navigation or refresh.
export const useSessionFile = (key) => {
  const [file, setFile] = useState(() => restoreFile(key));
  const [status, setStatus] = useState('saved');
  const reader = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const selectFile = useCallback(
    (next) => {
      if (reader.current?.readyState === 1) reader.current.abort();
      setFile(next || null);
      if (key) removeSession(key);
      if (!next || !key) {
        setStatus('saved');
        return;
      }
      setStatus('saving');
      const revision = getSessionRevision();
      const pending = new FileReader();
      reader.current = pending;
      pending.onload = () => {
        if (revision !== getSessionRevision()) return;
        const saved = writeSession(key, {
          name: next.name,
          type: next.type,
          lastModified: next.lastModified,
          data: String(pending.result).split(',')[1]
        });
        if (mounted.current) setStatus(saved ? 'saved' : 'unavailable');
      };
      pending.onerror = () => {
        if (mounted.current) setStatus('unavailable');
      };
      pending.readAsDataURL(next);
    },
    [key]
  );
  return [file, selectFile, status];
};
